import { Button, Icon } from '@snapchat/snap-design-system-marketing';
import type { FC, KeyboardEvent } from 'react';
import { useCallback, useEffect, useRef } from 'react';

import {
  inputContainerCss,
  inputFieldCss,
  sendButtonCss,
  sendIconDisabledCss,
  textareaCss,
} from './ChatInput.styles';

interface ChatInputProps {
  value: string;
  onChange: (value: string) => void;
  onSend: (value: string) => void;
  placeholder?: string;
  isDisabled?: boolean;
}

const maxRows = 2;

/** Auto-growing textarea input with send button for the AI Agent chat. Expands up to two lines. */
export const ChatInput: FC<ChatInputProps> = ({
  value,
  onChange,
  onSend,
  placeholder,
  isDisabled,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = textareaRef.current;

    if (!el) return;

    el.style.height = 'auto';
    el.style.overflowY = 'hidden';

    if (!value) return;

    const lineHeight = Number.parseFloat(getComputedStyle(el).lineHeight);
    const maxHeight = lineHeight * maxRows;
    const isOverflowing = el.scrollHeight > maxHeight;

    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
    el.style.overflowY = isOverflowing ? 'auto' : 'hidden';
  }, [value]);

  const isSendDisabled = !value.trim() || isDisabled;

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
        e.preventDefault();

        if (!isSendDisabled) {
          onSend(value);
        }
      }
    },
    [onSend, value, isSendDisabled]
  );

  return (
    <div className={inputContainerCss}>
      <div className={inputFieldCss}>
        <textarea
          ref={textareaRef}
          className={textareaCss}
          placeholder={placeholder ?? 'Ask a question...'}
          value={value}
          onChange={e => onChange(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={1}
        />
        <Button
          className={sendButtonCss}
          aria-label="Send message"
          onClick={() => {
            if (!isSendDisabled) {
              onSend(value);
            }
          }}
          disabled={isSendDisabled}
        >
          <Icon
            className={isSendDisabled ? sendIconDisabledCss : undefined}
            name="send-filled"
            size={24}
          />
        </Button>
      </div>
    </div>
  );
};

ChatInput.displayName = 'ChatInput';
